"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CurryValidator = exports.Validator = exports.getSingleValueValidationHandlers = exports.applyValidation = void 0;
var tslib_1 = require("tslib");
var lodash_1 = tslib_1.__importDefault(require("lodash"));
var path_utils_1 = require("@pinto/path-utils");
/**
 * 2 ways of using validation:
 * - the boring way:
 *  check.required('text') &&
 *      check.match('text', /foo/) &&
 *      check.whatever(...)
 *
 * - the curried way:
 *  check('text')
 *      .required()
 *      .match(/foo/)
 *      .whatever(...)
 */
function applyValidation(formValue, validator) {
    var v = new Validator({ value: formValue });
    validator({ values: formValue, check: v.getCheck() });
    return { errors: v.errors };
}
exports.applyValidation = applyValidation;
function getSingleValueValidationHandlers(value) {
    var v = new Validator({ value: { field: value } });
    return {
        check: v.curry('field'),
        getError: function () { return v.errors.field; },
    };
}
exports.getSingleValueValidationHandlers = getSingleValueValidationHandlers;
var Validator = /** @class */ (function () {
    function Validator(_a) {
        var _this = this;
        var value = _a.value, _b = _a.path, path = _b === void 0 ? null : _b, _c = _a.errors, errors = _c === void 0 ? {} : _c;
        /**
         * Validation handlers convention:
         *  - first argument validate always the context that contains { value, path, handlers, ... }
         *  - if invalid it returns an error message
         *  - a default error message should be the last argument
         */
        this.baseHandlers = {
            error: function (ctx, message) {
                if (message === void 0) { message = 'invalid'; }
                return message;
            },
            assert: function (_a, message) {
                var value = _a.value;
                if (message === void 0) { message = 'invalid'; }
                if (!value)
                    return message;
            },
            required: function (_a, message) {
                var value = _a.value, path = _a.path;
                if (message === void 0) { message = 'required'; }
                if (value == null || value === '')
                    return message;
            },
            match: function (_a, reg, message) {
                var value = _a.value, path = _a.path;
                if (message === void 0) { message = 'invalid format'; }
                if (!_this.handlers.required(path))
                    return message;
                if (!reg.test(value))
                    return message;
            },
            valid: function (_a, validator, message) {
                var value = _a.value;
                if (message === void 0) { message = 'invalid'; }
                var result = validator(value);
                if (result == null || result === true)
                    return;
                return result === false ? message : result;
            },
            size: function (_a, min, max, message) {
                var value = _a.value;
                if (min === void 0) { min = null; }
                if (max === void 0) { max = null; }
                if (message === void 0) { message = null; }
                var size = lodash_1.default.size(value);
                if (min != null && size < min)
                    return message || "Too few items. At least " + min + " required";
                if (max != null && size > max)
                    return message || "Too many items. At most " + max + " required";
            },
        };
        // These handlers don't return an error and usually apply some other kind of validation
        // e.g. field() returns a curried validation
        this.chainHandlers = {
            each: function (_a, childValidator) {
                var value = _a.value, path = _a.path;
                if (!Array.isArray(value) && !lodash_1.default.isPlainObject(value))
                    return;
                lodash_1.default.forEach(value, function (v, k) {
                    childValidator(_this.curry(path_utils_1.joinPaths(path, k)), v, k);
                });
            },
            field: function (_a, fieldPath) {
                var value = _a.value, path = _a.path;
                return _this.curry(path_utils_1.joinPaths(path, fieldPath));
            },
        };
        this.value = value;
        this.path = path;
        this.errors = errors;
        this.handlers = this.getHandlers();
    }
    Validator.prototype.extend = function (props) {
        return new Validator(tslib_1.__assign(tslib_1.__assign({}, lodash_1.default.pick(this, ['value', 'path', 'errors'])), props));
    };
    Validator.prototype.curry = function (path) {
        return new CurryValidator({
            value: lodash_1.default.get(this.value, path),
            path: path_utils_1.joinPaths(this.path, path),
            errors: this.errors,
        });
    };
    Validator.prototype.getCheck = function () {
        var _this = this;
        var check = function (path) { return _this.curry(path); };
        Object.assign(check, this.handlers);
        return check;
    };
    Validator.prototype.getHandlers = function () {
        var _this = this;
        var wrapValidator = function (type, callback) {
            return function (validator, validatorName) {
                var fn = function (path) {
                    var args = [];
                    for (var _i = 1; _i < arguments.length; _i++) {
                        args[_i - 1] = arguments[_i];
                    }
                    var absolutePath = path_utils_1.joinPaths(_this.path, path);
                    var value = path ? lodash_1.default.get(_this.value, path) : _this.value;
                    // console.log('[%s] %s =', validatorName, absolutePath, value, ...args);
                    var context = {
                        value: value,
                        path: path,
                        absolutePath: absolutePath,
                    };
                    return callback({
                        validator: validator,
                        validatorName: validatorName,
                        context: context,
                        result: validator.apply(void 0, tslib_1.__spreadArray([context], tslib_1.__read(args))),
                    });
                };
                fn.type = type;
                return fn;
            };
        };
        return tslib_1.__assign(tslib_1.__assign({}, lodash_1.default.mapValues(this.baseHandlers, wrapValidator('baseHandlers', function (_a) {
            var err = _a.result, absolutePath = _a.context.absolutePath;
            if (err)
                _this.errors[absolutePath] = err;
            return !err;
        }))), lodash_1.default.mapValues(this.chainHandlers, wrapValidator('chainHandlers', function (_a) {
            var result = _a.result;
            return result;
        })));
    };
    return Validator;
}());
exports.Validator = Validator;
var CurryValidator = /** @class */ (function (_super) {
    tslib_1.__extends(CurryValidator, _super);
    function CurryValidator(_a) {
        var e_1, _b;
        var _c = _a.isValid, isValid = _c === void 0 ? true : _c, props = tslib_1.__rest(_a, ["isValid"]);
        var _this = _super.call(this, props) || this;
        _this.isValid = isValid;
        var curryHandler = function (fn) {
            return function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                if (fn.type === 'baseHandlers') {
                    _this.isValid = _this.isValid && fn.apply(void 0, tslib_1.__spreadArray([null], tslib_1.__read(args)));
                    return _this.extend();
                }
                return fn.apply(void 0, tslib_1.__spreadArray([null], tslib_1.__read(args)));
            };
        };
        try {
            for (var _d = tslib_1.__values(Object.entries(_this.handlers)), _e = _d.next(); !_e.done; _e = _d.next()) {
                var _f = tslib_1.__read(_e.value, 2), handlerName = _f[0], fn = _f[1];
                if (Reflect.has(_this, handlerName)) {
                    throw new Error("Handler \"" + handlerName + "\" clashes with a class property");
                }
                _this[handlerName] = curryHandler(fn);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_e && !_e.done && (_b = _d.return)) _b.call(_d);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return _this;
    }
    CurryValidator.prototype.extend = function (props) {
        return new CurryValidator(tslib_1.__assign(tslib_1.__assign({}, lodash_1.default.pick(this, ['isValid', 'value', 'path', 'errors'])), props));
    };
    return CurryValidator;
}(Validator));
exports.CurryValidator = CurryValidator;
//# sourceMappingURL=validation.js.map